// @flow
import * as fp from "lodash/fp"
import type { Dispatch } from "redux"

import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import type { InsurancePricing } from "booking/services/prices/reducer"
import type { InsurancePassengers } from "./types"
import { loadInsurancePassengers, saveInsurancePassengers } from "./storeUtils"
import { getInsurancePassengers } from "./reducer"
import {
  INSURANCES,
  isInsuranceAllowedForNationality,
} from "../../../../../../app/scripts/tools/insurance"
import { getOriginalInsurance } from "booking/services/states/reducer"
import { INSURANCE_ORDERED } from "common/consts/insurance"
import Price from "common/records/Price"
import type { ReservationFields } from "booking/scenes/ReservationForm/services/fields/fields"

const INSURANCE_BASE_TO_PRICE_RATIO = 0.6
const INSURANCE_MARGIN_TO_PRICE_RATIO = 0.4

const mapValuesWithKey = fp.mapValues.convert({ cap: false })

export const insuranceLoaded = (insurancePrices: InsurancePricing) => (dispatch: Dispatch<any>) => {
  const payload = mapValuesWithKey((x, key) => {
    // $FlowFixMe
    const amount = insurancePrices.get(key)

    return {
      ...x,
      price: new Price({
        // FIXME: these magic numbers will come from BE
        base: amount * INSURANCE_BASE_TO_PRICE_RATIO,
        service: amount * INSURANCE_MARGIN_TO_PRICE_RATIO,
        amount,
      }),
    }
  }, INSURANCES)

  dispatch({
    type: "INSURANCE_LOADED",
    payload,
  })
}

export const setPassengersInsurance = (insurances: InsurancePassengers) => ({
  type: "SET_PASSENGERS_INSURANCE",
  payload: insurances,
})

export const unsetPassengerInsurance = (index: number) => ({
  type: "UNSET_PASSENGER_INSURANCE",
  payload: index,
})

export const changePassengerInsurance = (passengerIndex: number, type: string) => (
  dispatch: Dispatch<any>,
  getState: () => any,
) => {
  cuckoo.track(cuckooEvents.BOOKING_PASSENGER_INSURANCE_CHANGE, {
    passenger: passengerIndex + 1,
    value: type,
    step: "passenger",
  })

  cuckoo.infinario("Booking passenger", {
    category: "passenger",
    specification: passengerIndex + 1,
    status: "changed",
    field: "insurance",
    value: type,
    step: "passenger",
  })

  const ordered = getInsurancePassengers(getState())
  const payload = { ...ordered, [passengerIndex]: type }

  saveInsurancePassengers(payload)

  dispatch({
    type: "SET_PASSENGERS_INSURANCE",
    payload,
  })
}

export const updatePassengersInsurance = (fields: ReservationFields) => (
  dispatch: Dispatch<any>,
  getState: () => any,
) => {
  const originalInsurance = getOriginalInsurance(getState())

  // decide whether we use insurance from url or from session storage
  // we prefer originalInsurance which comes from url
  const insurancePassengers = originalInsurance.length
    ? originalInsurance.reduce((acc, val, i) => {
        if (val && INSURANCE_ORDERED[val]) {
          acc[i] = INSURANCE_ORDERED[val]
        }

        return acc
      }, {})
    : loadInsurancePassengers()

  // check if insurance is allowed for particular nationalities
  // values in url may be different from values in session storage during link copy, etc...
  const filteredInsurancePassengers = fields.passengers
    .map(fp.get("nationality.value"))
    .reduce((acc, nationality, i) => {
      const insurance = insurancePassengers[i]

      if (insurance && isInsuranceAllowedForNationality(nationality)) {
        acc[i] = insurance
      }

      return acc
    }, {})

  // finally set valid insurance for passengers
  dispatch(setPassengersInsurance(filteredInsurancePassengers))
}
